/**
 * The game's own best next move, read from the routes it already keeps: while
 * the player is on the gold path its next word is Global Optimal (gold); once
 * off it, the next word of the Local Optimal route from the current word
 * (purple). Pure: nothing here touches a store.
 */
import { isLocalOptimalMove } from "@synapse/engine";

import { optionWords } from "../components/board/options";
import type { GraphData } from "../services/dataLoader";
import { bestRoutePath } from "../utils/graphUtils";

export interface BestMoveState {
  currentWord: string | null;
  targetWord: string | null;
  /** The gold path (the store's optimalPath). */
  goldPath: string[];
  /** The Local Optimal route (the store's suggestedPathFromCurrent; starts at the current word). */
  localOptimalRoute: string[];
}

type Graph = Record<string, { edges?: Record<string, number> } | undefined>;

const after = (path: string[], word: string): string | null => {
  const i = path.indexOf(word);
  return i >= 0 && i + 1 < path.length ? path[i + 1] : null;
};

export function nextBestMove(s: BestMoveState): string | null {
  const cur = s.currentWord;
  if (!cur || cur === s.targetWord) return null;
  return after(s.goldPath, cur) ?? after(s.localOptimalRoute, cur);
}

/** The rest of the best route, current word first. */
export function remainingBestRoute(s: BestMoveState): string[] {
  const cur = s.currentWord;
  if (!cur) return [];
  const i = s.goldPath.indexOf(cur);
  if (i >= 0 && i + 1 < s.goldPath.length) return s.goldPath.slice(i);
  const j = s.localOptimalRoute.indexOf(cur);
  return j >= 0 ? s.localOptimalRoute.slice(j) : [];
}

/**
 * A Detour (neither Global Optimal nor Local Optimal) that can still reach
 * the target (most similar first), for testing the streak cool-down. Null
 * when there is none.
 */
export function detourOption(s: BestMoveState, graph: Graph): string | null {
  const cur = s.currentWord;
  const target = s.targetWord;
  if (!cur || !target) return null;
  const g = graph as GraphData;
  const best = new Set(
    [after(s.goldPath, cur), after(s.localOptimalRoute, cur)].filter(Boolean),
  );
  for (const w of optionWords(graph, cur)) {
    if (best.has(w) || w === target) continue;
    if (isLocalOptimalMove(g, cur, w, target)) continue;
    if (bestRoutePath(g, w, target).length > 0) return w;
  }
  return null;
}
